import { Component, Injector, OnInit, inject } from '@angular/core';
import { Database, get, ref } from '@angular/fire/database';
import { AuthService } from 'src/app/services/auth.service';
import { BaseComponent } from 'src/app/shared/baseComponent';
import { ScenarioModel, ComponentModel, LessonPlanStepModel, LessonPlanStepUIModel, InteractivityModel, GrassModel, FireModel, WindDirectionModel, TrackerModel, RolesModel } from '../vscenarioedit/ScenarioClasses';

// Define the value shape of fields in classes
interface FieldSpec {
  type: 'number' | 'string' | 'boolean' | 'object' | 'array';
}

@Component({
  selector: 'app-fbschema',
  templateUrl: './fbschema.component.html',
  styleUrls: ['./fbschema.component.css']
})
export class FBSchemaComponent extends BaseComponent implements OnInit {
  database = inject(Database);
  isAuthenticatedWithFirebase = false;

  // list of scenarios (raw objects) and ids for select
  scenarios: any[] = [];
  scenarioIds: string[] = [];

  // currently selected scenario id + object/json
  selectedScenarioId: string | null = null;
  selectedScenario: any = null;
  selectedScenarioJson = '';
  scenario: ScenarioModel
  foundFields:any[] = []
  missingFields: any[] = []
  additionalFields: any[] = [] // as per request (typo preserved)

  constructor(injector: Injector, private authService: AuthService) {
    super(injector);
  }

  async ngOnInit(): Promise<void> {
    // initialize firebase auth then load list
    this.isAuthenticatedWithFirebase = await this.authService.initializeAuth(() => this.onSuccessfulLogin());
    if (this.isAuthenticatedWithFirebase) {
      await this.loadScenarioList();
    }
  }

  async signInWithGoogle() {
    this.isAuthenticatedWithFirebase = await this.authService.signInWithGoogle();
    if (this.isAuthenticatedWithFirebase) {
      await this.onSuccessfulLogin();
    }
  }

  private async onSuccessfulLogin() {
    await this.loadScenarioList();
  }

  async loadScenarioList() {
    this.loader.show();
    this.scenarios = [];
    this.scenarioIds = [];
    const scenarioRef = ref(this.database, 'scenarios');
    const snapshot = await get(scenarioRef);
    if (snapshot.exists()) {
      const arr = BaseComponent.toArray(snapshot.val());
  // Filter: only include scenarios that have isVersion2 truthy
  const filtered = arr.filter((s: any) => !!s.isVersion2);
  this.scenarios = filtered;
  this.scenarioIds = filtered.map(s => s.id).filter((v: any) => v != null);
      this.scenarioIds.sort();
    }
    this.loader.hide();
  }

  async onSelectScenario(id: string) {
    if (!id) { return; }
    this.selectedScenarioId = id;
    this.loader.show();
    const scenarioRef = ref(this.database, 'scenarios/' + id);
    const snapshot = await get(scenarioRef);
    if (snapshot.exists()) {
      this.selectedScenario = snapshot.val();
      // pretty JSON
      this.selectedScenarioJson = JSON.stringify(this.selectedScenario, null, 2);
      this.missingFields = []
      this.additionalFields = []
      this.foundFields = []
      this.mapJsonToScenarioClass()
    } else {
      this.selectedScenario = null;
      this.selectedScenarioJson = '// Scenario not found';
    }
    this.loader.hide();
  }

  mapJsonToScenarioClass() {
    if (!this.selectedScenario) { return; }
    let s = this.selectedScenario // just for convenience
    // Map JSON fields to class properties
  this.scenario = new ScenarioModel()
    const scenarioMap = new Map<string, FieldSpec>();

    // Dynamically loop over current Scenario instance properties and infer type
    Object.keys(this.scenario).forEach(prop => {
      const val = (this.scenario as any)[prop];
      let inferred: FieldSpec['type'];
      if (Array.isArray(val)) inferred = 'array';
      else if (val === null || val === undefined) {
        // fall back to guessing from prop name conventions
        if (/^(is|has|show)/i.test(prop)) inferred = 'boolean';
        else if (/^(staging|.*X\d?|.*Y\d?)/i.test(prop)) inferred = 'number';
        else inferred = 'string';
      } else {
        const t = typeof val;
        if (t === 'string' || t === 'number' || t === 'boolean') inferred = t as any;
        else inferred = 'object';
      }
      this.mapScenarioField(s, this.scenario, prop, inferred, scenarioMap);
    });

    // Detect additional JSON fields not in our scenarioMap
    Object.keys(s).forEach(key => {
      if (!scenarioMap.has(key)) {
        this.additionalFields.push({ level: 'Scenario', field: key });
      }
    });

    // --- Component-level diffing (dynamic) ---
    if (Array.isArray(this.scenario.components)) {
      const template = new ComponentModel();
      const expectedComponentFields: string[] = Object.keys(template); // only fields with defaults
      this.scenario.components.forEach((comp: any, index: number) => {
        expectedComponentFields.forEach(f => {
          if (!Object.prototype.hasOwnProperty.call(comp, f) || comp[f] === undefined) {
            this.missingFields.push({ level: `Component[${index}]`, field: f });
          }
        });
        Object.keys(comp).forEach(k => {
          if (!expectedComponentFields.includes(k)) {
            this.additionalFields.push({ level: `Component[${index}]`, field: k });
          }
        });
      });
    }

    // --- Modifier-level diffing ---
    if (Array.isArray(this.scenario.modifiers)) {
      const expectedModifierFields: string[] = [
        'class','commands','componentname','enabled','seqNo'
      ];
      this.scenario.modifiers.forEach((mod: any, index: number) => {
        expectedModifierFields.forEach(f => {
          if (!Object.prototype.hasOwnProperty.call(mod, f) || mod[f] === undefined) {
            this.missingFields.push({ level: `Modifier[${index}]`, field: f });
          }
        });
        Object.keys(mod).forEach(k => {
          if (!expectedModifierFields.includes(k)) {
            this.additionalFields.push({ level: `Modifier[${index}]`, field: k });
          }
        });
      });
    }

    // --- Interactivity diffing (class-specific) ---
    if (Array.isArray(this.scenario.interactivities)) {
      this.scenario.interactivities.forEach((ia: any, index: number) => {
        const cls = ia?.class;
        // Choose template by class string
        let template: any;
        switch (cls) {
          case 'Grass':
          case 'GrassPatch': // legacy
            template = new GrassModel();
            break;
          case 'Fire': // keep legacy string value mapping
            template = new FireModel();
            break;
          case 'WindDirection':
            template = new WindDirectionModel();
            break;
          case 'Tracker':
            template = new TrackerModel();
            break;
          case 'Roles':
            template = new RolesModel();
            break;
          default:
            template = new InteractivityModel();
            break;
        }
        const baseKeys = Object.keys(template);
        // Missing fields
        baseKeys.forEach(k => {
          if (!Object.prototype.hasOwnProperty.call(ia, k) || ia[k] === undefined) {
            this.missingFields.push({ level: `Interactivity[${index}]`, field: k });
          }
        });
        // Additional fields
        Object.keys(ia || {}).forEach(k => {
          if (!baseKeys.includes(k)) {
            this.additionalFields.push({ level: `Interactivity[${index}]`, field: k });
          }
        });
      });
    }

    // --- LessonPlan step diffing (dynamic, class-specific) ---
    if (Array.isArray(this.scenario.lessonplan)) {
      this.scenario.lessonplan.forEach((step: any, index: number) => {
        const cls = step?.class;
  const template = cls === 'LessonPlanStepUI' ? new LessonPlanStepUIModel() : new LessonPlanStepModel();
        const baseKeys = Object.keys(template);
        // Missing
        baseKeys.forEach(k => {
          if (!Object.prototype.hasOwnProperty.call(step, k) || step[k] === undefined) {
            this.missingFields.push({ level: `LessonPlan[${index}]`, field: k });
          }
        });
        // Additional
        Object.keys(step || {}).forEach(k => {
          if (!baseKeys.includes(k)) {
            this.additionalFields.push({ level: `LessonPlan[${index}]`, field: k });
          }
        });
      });
    }
  }

  // Helper to map a single field, record missing fields, and register in scenario map
  private mapScenarioField(source: any, dest: ScenarioModel, jsonField: string, type: FieldSpec['type'], registry: Map<string, FieldSpec>, destField?: string) {
    this.foundFields.push({ level: 'Scenario', field: jsonField , type });
    registry.set(jsonField, { type });
    const targetProp = (destField || jsonField) as keyof ScenarioModel;

    if (!Object.prototype.hasOwnProperty.call(source, jsonField) || source[jsonField] === undefined) {
      this.missingFields.push({ level: 'Scenario', field: jsonField });
      return;
    }

    const value = source[jsonField];
    let assignValue: any = value;
    let typeMismatch = false;

    switch (type) {
      case 'array':
        if (Array.isArray(value)) {
          assignValue = [...value];
        } else {
          typeMismatch = true;
          assignValue = [];
        }
        break;
      case 'object':
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          assignValue = { ...value };
        } else {
          typeMismatch = true;
        }
        break;
      case 'number':
        if (typeof value !== 'number') typeMismatch = true;
        break;
      case 'boolean':
        if (typeof value !== 'boolean') typeMismatch = true;
        break;
      case 'string':
        if (typeof value !== 'string') typeMismatch = true;
        break;
    }

    (dest as any)[targetProp] = assignValue;
    if (typeMismatch) {
      this.additionalFields.push({ level: 'Scenario', field: jsonField + ' (type mismatch)' });
    }
  }

  // Generic extractor for displaying an identifier
  getItemId(item: any, placeholder: string = '(no id)'): string {
    if (!item) return placeholder;
    return item.id ?? item.name ?? item.displayName ?? item.type ?? placeholder;
  }

  componentHasDiff(index: number): boolean {
    const level = `Component[${index}]`;
    return this.missingFields.some(f => f.level === level) || this.additionalFields.some(f => f.level === level);
  }

  modifierHasDiff(index: number): boolean {
    const level = `Modifier[${index}]`;
    return this.missingFields.some(f => f.level === level) || this.additionalFields.some(f => f.level === level);
  }

  lessonPlanStepHasDiff(index: number): boolean {
    const level = `LessonPlan[${index}]`;
    return this.missingFields.some(f => f.level === level) || this.additionalFields.some(f => f.level === level);
  }

  interactivityHasDiff(index: number): boolean {
    const level = `Interactivity[${index}]`;
    return this.missingFields.some(f => f.level === level) || this.additionalFields.some(f => f.level === level);
  }
}
